import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { AuthorComposerUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { FieldAdornmentSlot } from "../../../../common/components/projectSlots";
import { Gender } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { TaxIdType } from "../../../../api/graphql";
import { AuthorComposerTaxIdCustomField } from "../custom/AuthorComposerTaxIdCustomField";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { InputPhone } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { InputDate } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { ProvinceStateCreate } from "../../../provinceState/components/create/ProvinceStateCreate";
import { ProvinceStateCreateStore } from "../../../provinceState/store";
import { AuthorComposerIpiCustomField } from "../custom/AuthorComposerIpiCustomField";

export type AuthorComposerPublicUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  hasPendingChanges?: boolean;
  firstName: string;
  firstNameSelectedValue?: { displayId: string; id: string } | null;
  onFirstNameChange?: (value: string) => void;
  firstNameError?: string;
  lastName: string;
  lastNameSelectedValue?: { displayId: string; id: string } | null;
  onLastNameChange?: (value: string) => void;
  lastNameError?: string;
  genderOptions: { id: string; displayId: string }[];
  genderError?: string;
  gender: Gender | "";
  onGenderChange?: (value: Gender | "") => void;
  taxIdTypeOptions: { id: string; displayId: string }[];
  taxIdTypeError?: string;
  taxIdType: TaxIdType | "";
  onTaxIdTypeChange?: (value: TaxIdType | "") => void;
  taxId: string;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxIdError?: string;
  email: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  emailError?: string;
  phone: string;
  phoneSelectedValue?: { displayId: string; id: string } | null;
  onPhoneChange?: (value: string) => void;
  phoneError?: string;
  nationalitySelectedValue?: { displayId: string; id: string } | null;
  onNationalityChange?: (value: string) => void;
  onNationalitySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  nationalityOptions?: { displayId: string; id: string }[];
  nationalityError?: string;
  dateOfBirth: string | null;
  dateOfBirthSelectedValue?: { displayId: string; id: string } | null;
  onDateOfBirthChange?: (value: string | null) => void;
  dateOfBirthError?: string;
  streetAndNumber: string;
  streetAndNumberSelectedValue?: { displayId: string; id: string } | null;
  onStreetAndNumberChange?: (value: string) => void;
  streetAndNumberError?: string;
  floor: string;
  floorSelectedValue?: { displayId: string; id: string } | null;
  onFloorChange?: (value: string) => void;
  floorError?: string;
  suite: string;
  suiteSelectedValue?: { displayId: string; id: string } | null;
  onSuiteChange?: (value: string) => void;
  suiteError?: string;
  postalCode: string;
  postalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPostalCodeChange?: (value: string) => void;
  postalCodeError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountryChange?: (value: string) => void;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceSelectedValue?: { displayId: string; id: string } | null;
  onProvinceChange?: (value: string) => void;
  onProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  provinceOptions?: { displayId: string; id: string }[];
  provinceError?: string;
  city: string;
  citySelectedValue?: { displayId: string; id: string } | null;
  onCityChange?: (value: string) => void;
  cityError?: string;
  aka: string;
  akaSelectedValue?: { displayId: string; id: string } | null;
  onAkaChange?: (value: string) => void;
  akaError?: string;
  ipi: string;
  ipiSelectedValue?: { displayId: string; id: string } | null;
  onIpiChange?: (value: string) => void;
  ipiError?: string;
  authorComposerUpdateStore: AuthorComposerUpdateStore;
};

export const AuthorComposerPublicUpdateForm: React.FC<AuthorComposerPublicUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      recordId,
      hasPendingChanges,
      firstName = "",
      firstNameError,
      onFirstNameChange = () => {},
      lastName = "",
      lastNameError,
      onLastNameChange = () => {},
      genderOptions = [],
      genderError,
      gender = "" as Gender | "",
      onGenderChange = () => {},
      taxIdTypeOptions = [],
      taxIdTypeError,
      taxIdType = "" as TaxIdType | "",
      onTaxIdTypeChange = () => {},
      taxId = "",
      taxIdError,
      onTaxIdChange = () => {},
      email = "",
      emailError,
      onEmailChange = () => {},
      phone = "",
      phoneError,
      onPhoneChange = () => {},
      onNationalitySelected = () => {},
      nationalityOptions = [],
      nationalitySelectedValue = null,
      nationalityError,
      onNationalityChange = () => {},
      dateOfBirth = null,
      dateOfBirthError,
      onDateOfBirthChange = () => {},
      streetAndNumber = "",
      streetAndNumberError,
      onStreetAndNumberChange = () => {},
      floor = "",
      floorError,
      onFloorChange = () => {},
      suite = "",
      suiteError,
      onSuiteChange = () => {},
      postalCode = "",
      postalCodeError,
      onPostalCodeChange = () => {},
      onCountrySelected = () => {},
      countryOptions = [],
      countrySelectedValue = null,
      countryError,
      onCountryChange = () => {},
      onProvinceSelected = () => {},
      provinceOptions = [],
      provinceSelectedValue = null,
      provinceError,
      onProvinceChange = () => {},
      city = "",
      cityError,
      onCityChange = () => {},
      aka = "",
      akaError,
      onAkaChange = () => {},
      ipi = "",
      ipiError,
      onIpiChange = () => {},
      authorComposerUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              recordId={recordId}
              values={{
                firstName,
                lastName,
                gender,
                taxIdType,
                taxId,
                email,
                phone,
                nationality: nationalitySelectedValue,
                dateOfBirth,
                streetAndNumber,
                floor,
                suite,
                postalCode,
                country: countrySelectedValue,
                province: provinceSelectedValue,
                city,
                aka,
                ipi,
              }}
            >
              <FormExtras
                entity="AuthorComposer"
                mode="update"
                recordId={recordId}
                values={{
                  firstName,
                  lastName,
                  gender,
                  taxIdType,
                  taxId,
                  email,
                  phone,
                  nationality: nationalitySelectedValue,
                  dateOfBirth,
                  streetAndNumber,
                  floor,
                  suite,
                  postalCode,
                  country: countrySelectedValue,
                  province: provinceSelectedValue,
                  city,
                  aka,
                  ipi,
                }}
                options={{
                  nationality: nationalityOptions,
                  country: countryOptions,
                  province: provinceOptions,
                }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "firstName":
                      onFirstNameChange?.(valor);
                      break;
                    case "lastName":
                      onLastNameChange?.(valor);
                      break;
                    case "gender":
                      onGenderChange?.(valor);
                      break;
                    case "taxIdType":
                      onTaxIdTypeChange?.(valor);
                      break;
                    case "taxId":
                      onTaxIdChange?.(valor);
                      break;
                    case "email":
                      onEmailChange?.(valor);
                      break;
                    case "phone":
                      onPhoneChange?.(valor);
                      break;
                    case "nationality":
                      onNationalitySelected?.(valor);
                      break;
                    case "dateOfBirth":
                      onDateOfBirthChange?.(valor);
                      break;
                    case "streetAndNumber":
                      onStreetAndNumberChange?.(valor);
                      break;
                    case "floor":
                      onFloorChange?.(valor);
                      break;
                    case "suite":
                      onSuiteChange?.(valor);
                      break;
                    case "postalCode":
                      onPostalCodeChange?.(valor);
                      break;
                    case "country":
                      onCountrySelected?.(valor);
                      break;
                    case "province":
                      onProvinceSelected?.(valor);
                      break;
                    case "city":
                      onCityChange?.(valor);
                      break;
                    case "aka":
                      onAkaChange?.(valor);
                      break;
                    case "ipi":
                      onIpiChange?.(valor);
                      break;
                  }
                }}
              />
              <GroupContainer
                id="personalInformation"
                errorText={authorComposerUpdateStore.getCardErrors(
                  "personalInformation"
                )}
                title={
                  langStore.groupAuthorComposerPublicPersonalInformationLabel
                }
                collapsible={true}
                expanded={authorComposerUpdateStore.getCardState(
                  "personalInformation"
                )}
                toggleExpand={() =>
                  authorComposerUpdateStore.toggleCardState(
                    "personalInformation"
                  )
                }
              >
                <FormControl fullWidth>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="firstName"
                    mode="update"
                    recordId={recordId}
                    value={firstName}
                  >
                    <InputString
                      autoFocus
                      value={firstName}
                      error={Boolean(firstNameError)}
                      warning={
                        !firstNameError &&
                        Boolean(authorComposerUpdateStore.warnings["firstName"])
                      }
                      helperText={
                        firstNameError ||
                        authorComposerUpdateStore.warnings["firstName"]?.message
                      }
                      label={langStore.AuthorComposerFirstNameLabel}
                      onChange={(value: string) => onFirstNameChange(value)}
                      description={langStore.AuthorComposerFirstNameDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="lastName"
                    mode="update"
                    recordId={recordId}
                    value={lastName}
                  >
                    <InputString
                      value={lastName}
                      error={Boolean(lastNameError)}
                      warning={
                        !lastNameError &&
                        Boolean(authorComposerUpdateStore.warnings["lastName"])
                      }
                      helperText={
                        lastNameError ||
                        authorComposerUpdateStore.warnings["lastName"]?.message
                      }
                      label={langStore.AuthorComposerLastNameLabel}
                      onChange={(value: string) => onLastNameChange(value)}
                      description={langStore.AuthorComposerLastNameDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="gender"
                    mode="update"
                    recordId={recordId}
                    value={gender}
                  >
                    <InputReference
                      id="gender"
                      disabled={false}
                      error={Boolean(genderError)}
                      helperText={
                        genderError ||
                        authorComposerUpdateStore.warnings["gender"]?.message
                      }
                      label={langStore.AuthorComposerGenderLabel}
                      options={genderOptions}
                      nullText={langStore.optionalFieldHint}
                      selectedValue={
                        gender
                          ? {
                              id: gender,
                              displayId:
                                genderOptions.find(
                                  (o: { id: string }) => o.id === gender
                                )?.displayId || gender,
                            }
                          : null
                      }
                      onChange={() => {}}
                      onSelect={(
                        value: { id: string; displayId: string } | null
                      ) =>
                        onGenderChange(
                          (value?.id as Gender | "") || ("" as Gender | "")
                        )
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="taxIdType"
                    mode="update"
                    recordId={recordId}
                    value={taxIdType}
                  >
                    <InputReference
                      id="taxIdType"
                      disabled={false}
                      error={Boolean(taxIdTypeError)}
                      helperText={
                        taxIdTypeError ||
                        authorComposerUpdateStore.warnings["taxIdType"]?.message
                      }
                      label={langStore.AuthorComposerTaxIdTypeLabel}
                      options={taxIdTypeOptions}
                      nullText={langStore.optionalFieldHint}
                      selectedValue={
                        taxIdType
                          ? {
                              id: taxIdType,
                              displayId:
                                taxIdTypeOptions.find(
                                  (o: { id: string }) => o.id === taxIdType
                                )?.displayId || taxIdType,
                            }
                          : null
                      }
                      onChange={() => {}}
                      onSelect={(
                        value: { id: string; displayId: string } | null
                      ) =>
                        onTaxIdTypeChange(
                          (value?.id as TaxIdType | "") ||
                            ("" as TaxIdType | "")
                        )
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="taxId"
                    mode="update"
                    recordId={recordId}
                    value={taxId}
                  >
                    <AuthorComposerTaxIdCustomField
                      value={taxId}
                      error={Boolean(taxIdError)}
                      helperText={
                        taxIdError ||
                        authorComposerUpdateStore.warnings["taxId"]?.message
                      }
                      {...{ optional: true }}
                      label={langStore.AuthorComposerTaxIdLabel}
                      description={langStore.AuthorComposerTaxIdDescription}
                      onChange={(value: string) => onTaxIdChange(value)}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="email"
                    mode="update"
                    recordId={recordId}
                    value={email}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <InputString
                        value={email}
                        error={Boolean(emailError)}
                        warning={
                          !emailError &&
                          Boolean(authorComposerUpdateStore.warnings["email"])
                        }
                        helperText={
                          emailError ||
                          authorComposerUpdateStore.warnings["email"]?.message
                        }
                        label={langStore.AuthorComposerEmailLabel}
                        onChange={(value: string) => onEmailChange(value)}
                        description={langStore.AuthorComposerEmailDescription}
                      />
                      <EmailVerificationIndicator
                        email={email}
                        entityName="AuthorComposer"
                        recordId={recordId}
                        validationMode={true}
                        hasPendingChanges={hasPendingChanges}
                      />
                    </Box>
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="phone"
                    mode="update"
                    recordId={recordId}
                    value={phone}
                  >
                    <InputPhone
                      value={phone}
                      error={Boolean(phoneError)}
                      helperText={
                        phoneError ||
                        authorComposerUpdateStore.warnings["phone"]?.message
                      }
                      label={langStore.AuthorComposerPhoneLabel}
                      onChange={(value: string) => onPhoneChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerPhoneDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="nationality"
                    mode="update"
                    recordId={recordId}
                    value={nationalitySelectedValue}
                  >
                    <InputReference
                      id="nationality"
                      label={langStore.AuthorComposerNationalityLabel}
                      error={Boolean(nationalityError)}
                      helperText={
                        nationalityError ||
                        authorComposerUpdateStore.warnings["nationality"]
                          ?.message
                      }
                      selectedValue={nationalitySelectedValue}
                      onChange={(value: string) => onNationalityChange(value)}
                      onSelect={(
                        value: { displayId: string; id: string } | null
                      ) => onNationalitySelected(value)}
                      options={nationalityOptions}
                      description={
                        langStore.AuthorComposerNationalityDescription
                      }
                      nullText={langStore.optionalFieldHint}
                      canCreate={rootStore.authStore.hasPermissions(
                        "country_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new CountryCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <CountryCreate
                              countryCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewCountry"
                          cancelMethod="cancelNewCountry"
                        />
                      )}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="dateOfBirth"
                    mode="update"
                    recordId={recordId}
                    value={dateOfBirth}
                  >
                    <InputDate
                      value={dateOfBirth}
                      error={Boolean(dateOfBirthError)}
                      warning={
                        !dateOfBirthError &&
                        Boolean(
                          authorComposerUpdateStore.warnings["dateOfBirth"]
                        )
                      }
                      helperText={
                        dateOfBirthError ||
                        authorComposerUpdateStore.warnings["dateOfBirth"]
                          ?.message
                      }
                      label={langStore.AuthorComposerDateOfBirthLabel}
                      nullText={langStore.optionalFieldHint}
                      description={
                        langStore.AuthorComposerDateOfBirthDescription
                      }
                      onChange={(value: string | null | null) =>
                        onDateOfBirthChange(value)
                      }
                    />
                  </FieldAdornmentSlot>
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="address"
                errorText={authorComposerUpdateStore.getCardErrors("address")}
                title={langStore.groupAuthorComposerPublicAddressLabel}
                collapsible={true}
                expanded={authorComposerUpdateStore.getCardState("address")}
                toggleExpand={() =>
                  authorComposerUpdateStore.toggleCardState("address")
                }
              >
                <FormControl fullWidth>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="streetAndNumber"
                    mode="update"
                    recordId={recordId}
                    value={streetAndNumber}
                  >
                    <InputString
                      value={streetAndNumber}
                      error={Boolean(streetAndNumberError)}
                      warning={
                        !streetAndNumberError &&
                        Boolean(
                          authorComposerUpdateStore.warnings["streetAndNumber"]
                        )
                      }
                      helperText={
                        streetAndNumberError ||
                        authorComposerUpdateStore.warnings["streetAndNumber"]
                          ?.message
                      }
                      label={langStore.AuthorComposerStreetAndNumberLabel}
                      onChange={(value: string) =>
                        onStreetAndNumberChange(value)
                      }
                      nullText={langStore.optionalFieldHint}
                      description={
                        langStore.AuthorComposerStreetAndNumberDescription
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="floor"
                    mode="update"
                    recordId={recordId}
                    value={floor}
                  >
                    <InputString
                      value={floor}
                      error={Boolean(floorError)}
                      warning={
                        !floorError &&
                        Boolean(authorComposerUpdateStore.warnings["floor"])
                      }
                      helperText={
                        floorError ||
                        authorComposerUpdateStore.warnings["floor"]?.message
                      }
                      label={langStore.AuthorComposerFloorLabel}
                      onChange={(value: string) => onFloorChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerFloorDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="suite"
                    mode="update"
                    recordId={recordId}
                    value={suite}
                  >
                    <InputString
                      value={suite}
                      error={Boolean(suiteError)}
                      warning={
                        !suiteError &&
                        Boolean(authorComposerUpdateStore.warnings["suite"])
                      }
                      helperText={
                        suiteError ||
                        authorComposerUpdateStore.warnings["suite"]?.message
                      }
                      label={langStore.AuthorComposerSuiteLabel}
                      onChange={(value: string) => onSuiteChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerSuiteDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="postalCode"
                    mode="update"
                    recordId={recordId}
                    value={postalCode}
                  >
                    <InputString
                      value={postalCode}
                      error={Boolean(postalCodeError)}
                      warning={
                        !postalCodeError &&
                        Boolean(
                          authorComposerUpdateStore.warnings["postalCode"]
                        )
                      }
                      helperText={
                        postalCodeError ||
                        authorComposerUpdateStore.warnings["postalCode"]
                          ?.message
                      }
                      label={langStore.AuthorComposerPostalCodeLabel}
                      onChange={(value: string) => onPostalCodeChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={
                        langStore.AuthorComposerPostalCodeDescription
                      }
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="country"
                    mode="update"
                    recordId={recordId}
                    value={countrySelectedValue}
                  >
                    <InputReference
                      id="country"
                      label={langStore.AuthorComposerCountryLabel}
                      error={Boolean(countryError)}
                      helperText={
                        countryError ||
                        authorComposerUpdateStore.warnings["country"]?.message
                      }
                      selectedValue={countrySelectedValue}
                      onChange={(value: string) => onCountryChange(value)}
                      onSelect={(
                        value: { displayId: string; id: string } | null
                      ) => onCountrySelected(value)}
                      options={countryOptions}
                      description={langStore.AuthorComposerCountryDescription}
                      nullText={langStore.optionalFieldHint}
                      canCreate={rootStore.authStore.hasPermissions(
                        "country_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new CountryCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <CountryCreate
                              countryCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewCountry"
                          cancelMethod="cancelNewCountry"
                        />
                      )}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="province"
                    mode="update"
                    recordId={recordId}
                    value={provinceSelectedValue}
                  >
                    <InputReference
                      id="province"
                      label={langStore.AuthorComposerProvinceLabel}
                      error={Boolean(provinceError)}
                      helperText={
                        provinceError ||
                        authorComposerUpdateStore.warnings["province"]?.message
                      }
                      selectedValue={provinceSelectedValue}
                      onChange={(value: string) => onProvinceChange(value)}
                      onSelect={(
                        value: { displayId: string; id: string } | null
                      ) => onProvinceSelected(value)}
                      options={provinceOptions}
                      description={langStore.AuthorComposerProvinceDescription}
                      nullText={langStore.optionalFieldHint}
                      canCreate={rootStore.authStore.hasPermissions(
                        "provinceState_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new ProvinceStateCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <ProvinceStateCreate
                              provinceStateCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewProvinceState"
                          cancelMethod="cancelNewProvinceState"
                        />
                      )}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="city"
                    mode="update"
                    recordId={recordId}
                    value={city}
                  >
                    <InputString
                      value={city}
                      error={Boolean(cityError)}
                      warning={
                        !cityError &&
                        Boolean(authorComposerUpdateStore.warnings["city"])
                      }
                      helperText={
                        cityError ||
                        authorComposerUpdateStore.warnings["city"]?.message
                      }
                      label={langStore.AuthorComposerCityLabel}
                      onChange={(value: string) => onCityChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerCityDescription}
                    />
                  </FieldAdornmentSlot>
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="professionalInformation"
                errorText={authorComposerUpdateStore.getCardErrors(
                  "professionalInformation"
                )}
                title={
                  langStore.groupAuthorComposerPublicProfessionalInformationLabel
                }
                collapsible={true}
                expanded={authorComposerUpdateStore.getCardState(
                  "professionalInformation"
                )}
                toggleExpand={() =>
                  authorComposerUpdateStore.toggleCardState(
                    "professionalInformation"
                  )
                }
              >
                <FormControl fullWidth>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="aka"
                    mode="update"
                    recordId={recordId}
                    value={aka}
                  >
                    <InputString
                      value={aka}
                      error={Boolean(akaError)}
                      warning={
                        !akaError &&
                        Boolean(authorComposerUpdateStore.warnings["aka"])
                      }
                      helperText={
                        akaError ||
                        authorComposerUpdateStore.warnings["aka"]?.message
                      }
                      label={langStore.AuthorComposerAkaLabel}
                      onChange={(value: string) => onAkaChange(value)}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.AuthorComposerAkaDescription}
                    />
                  </FieldAdornmentSlot>
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="ipi"
                    mode="update"
                    recordId={recordId}
                    value={ipi}
                  >
                    <AuthorComposerIpiCustomField
                      value={ipi}
                      error={Boolean(ipiError)}
                      helperText={
                        ipiError ||
                        authorComposerUpdateStore.warnings["ipi"]?.message
                      }
                      {...{ optional: true }}
                      label={langStore.AuthorComposerIpiLabel}
                      description={langStore.AuthorComposerIpiDescription}
                      onChange={(value: string) => onIpiChange(value)}
                    />
                  </FieldAdornmentSlot>
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
